import React, { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import { useProject } from "@/context/ProjectContext";
import api from "@/lib/api";
import { ChevronDown, ChevronRight } from "lucide-react";

const ENGINE_LABELS = {
  openai: "ChatGPT",
  anthropic: "Claude",
  gemini: "Gemini",
  perplexity: "Perplexity*",
  google_aio: "AI Overviews*",
};

export default function Runs() {
  const { current, projects, selectProject } = useProject();
  const [runs, setRuns] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});

  useEffect(() => {
    if (!current) return;
    api
      .get(`/projects/${current.project_id}/runs`)
      .then((r) => setRuns(r.data));
  }, [current]);

  const toggle = async (id) => {
    if (expanded === id) {
      setExpanded(null);
      return;
    }
    setExpanded(id);
    if (!details[id]) {
      const r = await api.get(`/runs/${id}`);
      setDetails((d) => ({ ...d, [id]: r.data }));
    }
  };

  return (
    <DashboardShell project={current} projects={projects} onProjectChange={selectProject}>
      <div className="mb-8">
        <div className="label-eyebrow">History</div>
        <h1 className="display mt-2 text-4xl font-black tracking-tighter">
          Run history
        </h1>
        <p className="mt-2 text-zinc-600 text-sm max-w-xl">
          Every prompt, every engine, every answer — preserved for auditing.
        </p>
      </div>

      <section className="card-flat overflow-hidden" data-testid="runs-table">
        <div className="bg-[#fafafa] border-b border-zinc-200 px-5 py-3 grid grid-cols-12 gap-4 label-eyebrow">
          <div className="col-span-3">When</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Engines</div>
          <div className="col-span-2">Results</div>
          <div className="col-span-2">BVS</div>
          <div className="col-span-1 text-right">#</div>
        </div>
        <div className="divide-y divide-zinc-200">
          {runs.map((r) => {
            const open = expanded === r.run_id;
            const d = details[r.run_id];
            return (
              <div key={r.run_id}>
                <button
                  onClick={() => toggle(r.run_id)}
                  data-testid={`run-row-${r.run_id}`}
                  className="w-full text-left px-5 py-4 grid grid-cols-12 gap-4 items-center text-sm hover:bg-[#fafafa] transition-colors"
                >
                  <div className="col-span-3 mono text-xs">
                    {new Date(r.created_at).toLocaleString()}
                  </div>
                  <div className="col-span-2">
                    <Status status={r.status} />
                  </div>
                  <div className="col-span-2 text-zinc-700">
                    {r.engines?.length || 0}
                  </div>
                  <div className="col-span-2 text-zinc-700">
                    {r.results_count || 0}
                  </div>
                  <div className="col-span-2 tabular-nums">
                    {r.bvs_score?.toFixed(1) ?? "—"}
                  </div>
                  <div className="col-span-1 text-right text-zinc-400">
                    {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </div>
                </button>
                {open && d && (
                  <div className="px-5 pb-5 bg-[#fafafa]">
                    <div className="rounded-[4px] border border-zinc-200 bg-white divide-y divide-zinc-200">
                      {d.results?.map((res) => (
                        <div
                          key={res.result_id}
                          className="p-4 grid grid-cols-12 gap-4 text-xs"
                        >
                          <div className="col-span-2 mono">
                            {ENGINE_LABELS[res.engine] || res.engine}
                          </div>
                          <div className="col-span-6">
                            <div className="text-zinc-800 mb-1 font-medium">
                              {res.prompt_text}
                            </div>
                            <div className="text-zinc-500 line-clamp-3 whitespace-pre-wrap">
                              {res.response_text || res.error || "(empty)"}
                            </div>
                          </div>
                          <div className="col-span-4 grid grid-cols-3 gap-2 text-[11px]">
                            <StatChip
                              label="Mention"
                              on={res.mentioned_brand}
                            />
                            <StatChip
                              label="Cited"
                              on={res.cited_with_link}
                            />
                            <StatChip
                              label={res.is_mock ? "Mock" : "Live"}
                              on={!res.is_mock}
                              positiveOnly
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {!runs.length && (
            <div className="p-8 text-sm text-zinc-500 text-center">
              No runs yet. Trigger one from the Overview page.
            </div>
          )}
        </div>
      </section>
    </DashboardShell>
  );
}

function Status({ status }) {
  const map = {
    running: "bg-amber-50 text-amber-700 border-amber-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    failed: "bg-rose-50 text-rose-700 border-rose-200",
  };
  return (
    <span
      className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border ${
        map[status] || "bg-zinc-50 text-zinc-700 border-zinc-200"
      }`}
    >
      {status}
    </span>
  );
}

function StatChip({ label, on, positiveOnly }) {
  const cls = on
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : positiveOnly
    ? "bg-zinc-50 text-zinc-500 border-zinc-200"
    : "bg-zinc-50 text-zinc-600 border-zinc-200";
  return (
    <span className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border ${cls}`}>
      {label} {on ? "✓" : "–"}
    </span>
  );
}
